import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './SearchResults.css';
import api from '../../api';
import {
  FaShoppingCart,
  FaCreditCard,
  FaStar,
  FaHeart,
  FaPlay,
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import UsersTopbar from '../UsersTopbar/UsersTopbar';
import useBlockCheck from '../../hooks/useBlockCheck'; // Import the hook

const SearchResults = () => {
  useBlockCheck('user'); // 🔥 Prevents blocked users from accessing this page
  const [searchParams] = useSearchParams();
  const query = searchParams.get('query'); // Get search query from URL
  const navigate = useNavigate();
  const { token } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState([]);
  const [cart, setCart] = useState([]);
  const [purchasedCourses, setPurchasedCourses] = useState([]);

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!query) return;
      setLoading(true);
      try {
        const response = await axios.get(`${api.getAllCourses}?search=${query}`);
        setCourses(response.data.courses || []);

        if (token) {
          const userData = await axios.get(`${api.getCartAndWishlist}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setCart(userData.data.cart || []);
          setWishlist(userData.data.wishlist || []);

          const purchased = await axios.get(api.getPurchasedCourses, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setPurchasedCourses(purchased.data.courses || []);
        }
      } catch (error) {
        console.error('Error fetching search results:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [query, token]);

  const handleAddToCart = async (courseId) => {
    try {
      await axios.post(`${api.addToCart}`, { courseId }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updatedCart = await axios.get(`${api.getCartAndWishlist}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCart(updatedCart.data.cart);
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const handleBuyNow = (courseId) => {
    navigate(`/checkout?courseId=${courseId}`);
  };

  const handleWishlistToggle = async (courseId) => {
    try {
      if (wishlist.some((item) => item._id === courseId)) {
        await axios.delete(`${api.addToWishlist}/${courseId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setWishlist((prev) => prev.filter((item) => item._id !== courseId));
      } else {
        await axios.post(`${api.addToWishlist}`, { courseId }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setWishlist((prev) => [...prev, { _id: courseId }]);
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
    }
  };

  const isPurchased = (courseId) => {
    return purchasedCourses.some((course) => course._id === courseId);
  };

  if (loading) return <p className="loading-text">Loading search results...</p>;

  return (
    <div>
      <UsersTopbar />
      <div className="search-results-container">
        <h2>Search Results for: "{query}"</h2>
        {courses.length === 0 ? (
          <p>No courses found for your search.</p>
        ) : (
          <div className="search-results-grid">
            {courses.map((course) => (
              <div key={course._id} className="search-course-card">

                <div
                  onClick={() => navigate(`/courses/${course._id}`)}
                  className="search-course-card-image-wrapper"
                >
                  {course.image && (
                    <img
                      src={`${api.img + course.image}`}
                      alt={course.title}
                      className="search-course-card-image"
                    />

                  )} <div className="play-button-overlay">
                    <FaPlay className="play-icon" />
                  </div>
                  {!isPurchased(course._id) && (
                    <button className="search-results-wishlist-button" onClick={() => handleWishlistToggle(course._id)}>
                      <FaHeart className={`search-results-wishlist-icon ${wishlist.some((item) => item._id === course._id) ? 'filled' : ''}`} />
                    </button>
                  )}

                  <div className="category-course-price-box">
                    <span className="category-course-offer-price">
                      {course.offerPrice === 0 ? (
                        <span className="category-price-free">Free</span>
                      ) : (
                        `₹ ${course.offerPrice}`
                      )}
                    </span>
                    <span className="category-price-original">
                      ₹{course.actualPrice}
                    </span>
                    {course.offerPrice !== -1 && (
                      <span className="category-price-discount" style={{ marginLeft: '8px' }}>
                        {Math.round(((course.actualPrice - course.offerPrice) / course.actualPrice) * 100)}% Off
                      </span>
                    )}
                  </div>
                  <h3 className="search-course-card-h3">{course.title}</h3>
                </div>  
                {/* <p className="search-course-description">{course.description}</p> */}
                {/* <p>Instructor: {course.instructor}</p> */}
                <div className="course-rating">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <FaStar key={index} className={index < Math.round(course.averageRating) ? 'star filled' : 'star'} />
                  ))}
                </div>
                <div className="search-card-buttons">
                  {isPurchased(course._id) ? (
                    <button className="search-results-go-to-course" onClick={() => navigate(`/course/${course._id}`)}>
                       Go to Course
                    </button>
                  ) : (
                    <>
                      {cart.some((item) => item._id === course._id) ? (
                        <button className="search-results-go-to-cart" onClick={() => navigate('/cart')}>
                          <FaShoppingCart /> Go to Cart
                        </button>
                      ) : (
                        <button className="search-results-add-to-cart" onClick={() => handleAddToCart(course._id)}>
                          <FaShoppingCart /> Add to Cart
                        </button>
                      )}
                      <button className="search-results-buy-now" onClick={() => handleBuyNow(course._id)}>
                        <FaCreditCard /> Buy Now
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
